import type { MasterListDocument } from '@/entities/Document';
import { getExternalRevisionDisplay } from './external-revision-display';
import {
  QUALITY_RECORD_HEADER_CLASS,
  QUALITY_RECORD_HEADER_LOGO_CLASS,
  QUALITY_RECORD_HEADER_LABEL_CLASS,
  QUALITY_RECORD_HEADER_CODE_CLASS,
  QUALITY_RECORD_HEADER_REVISION_CLASS,
  qualityRecordHeaderTitleClass,
} from '@/shared/lib/print';

interface BuildMasterListPrintHtmlParams {
  code: string;
  documents: MasterListDocument[];
  isExternalList: boolean;
  logoSrc: string;
  revision: string;
  revisionColumnLabel: string;
  styles: string;
  title: string;
}

const escapeHtml = (value: string | null | undefined) =>
  String(value ?? '-')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getRevisionDisplay = (document: MasterListDocument, isExternalList: boolean) =>
  isExternalList ? getExternalRevisionDisplay(document.externalRevision) : document.revision;

const renderFormatDisplay = (value: string | null | undefined) => escapeHtml(value).replace(/\//g, '/<wbr>');

const renderPrintRows = (documents: MasterListDocument[], isExternalList: boolean) => {
  if (documents.length === 0) {
    return '<div class="text-center py-8"><div class="text-gray-500 text-lg">Nenhum documento encontrado</div></div>';
  }

  return documents
    .map(
      (document) => `
        <div class="grid grid-cols-6 gap-4 mb-4 break-inside-avoid">
          <div class="rounded-2xl border flex items-center p-4 border-border-color bg-gray-02/20 min-h-[62px] min-w-0 text-sm whitespace-nowrap">${escapeHtml(document.prefix)}</div>
          <div class="rounded-2xl border flex items-center p-4 border-border-color bg-gray-02/20 min-h-[62px] min-w-0 text-sm break-normal [hyphens:none]">${escapeHtml(document.documentName)}</div>
          <div class="rounded-2xl border flex items-center p-4 border-border-color bg-gray-02/20 min-h-[62px] min-w-0 text-sm whitespace-nowrap">${escapeHtml(getRevisionDisplay(document, isExternalList))}</div>
          <div class="rounded-2xl border flex items-center p-4 border-border-color bg-gray-02/20 min-h-[62px] min-w-0 text-sm whitespace-nowrap">${escapeHtml(document.validityDate)}</div>
          <div class="rounded-2xl border flex items-center p-4 border-border-color bg-gray-02/20 min-h-[62px] min-w-0 text-sm whitespace-nowrap">${escapeHtml(document.status)}</div>
          <div class="rounded-2xl border flex items-center p-4 border-border-color bg-gray-02/20 min-h-[62px] min-w-0 text-sm">
            <span class="block min-w-0 max-w-full whitespace-normal break-normal text-xs leading-4">${renderFormatDisplay(document.format)}</span>
          </div>
        </div>
      `
    )
    .join('');
};

export const buildMasterListPrintHtml = ({
  code,
  documents,
  isExternalList,
  logoSrc,
  revision,
  revisionColumnLabel,
  styles,
  title,
}: BuildMasterListPrintHtmlParams) => `
  <html>
    <head>
      <title>${escapeHtml(title)}</title>
      ${styles}
    </head>
    <body class="!block !m-0 !min-h-0 !min-w-0 !w-full !box-border !bg-white-01 !p-6 text-gray-07 font-base print:!block print:!min-h-0">
      <main class="bg-white-01 w-full max-w-full">
        <table class="w-full border-collapse border border-gray-400 mb-6 break-inside-avoid ${QUALITY_RECORD_HEADER_CLASS}">
          <thead>
            <tr>
              <td rowspan="2" class="border border-gray-400 py-4 w-60 text-center bg-white-01 ${QUALITY_RECORD_HEADER_LOGO_CLASS}">
                <div class="flex flex-col items-center">
                  <img src="${escapeHtml(logoSrc)}" alt="Logo HTM" class="block max-w-[170px] max-h-[84px] mx-auto">
                </div>
              </td>
              <td class="border border-gray-400 p-4 text-center ${QUALITY_RECORD_HEADER_LABEL_CLASS}">
                <div class="text-lg font-semibold text-gray-800 ${QUALITY_RECORD_HEADER_LABEL_CLASS}">DADOS</div>
              </td>
              <td class="border border-gray-400 p-2 text-center w-40 ${QUALITY_RECORD_HEADER_CODE_CLASS}">
                <div class="text-sm font-semibold ${QUALITY_RECORD_HEADER_CODE_CLASS}">${escapeHtml(code)}</div>
              </td>
              <td class="border border-gray-400 p-2 text-center w-40 ${QUALITY_RECORD_HEADER_REVISION_CLASS}">
                <div class="text-sm font-semibold ${QUALITY_RECORD_HEADER_REVISION_CLASS}">${escapeHtml(revision)}</div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colspan="3" class="border border-gray-400 p-4 text-center">
                <div class="text-lg font-semibold text-gray-800 ${qualityRecordHeaderTitleClass(title)}">${escapeHtml(title)}</div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="border-t-1 border-light-gray-xx my-6"></div>
        <section>
          <div class="grid grid-cols-6 gap-4 mb-6">
            <div class="font-semibold text-sm text-[var(--theme-secondary-ink)] text-left whitespace-nowrap">Prefixo:</div>
            <div class="font-semibold text-sm text-[var(--theme-secondary-ink)] text-left">Nome do Documento:</div>
            <div class="font-semibold text-sm text-[var(--theme-secondary-ink)] text-left whitespace-nowrap">${escapeHtml(revisionColumnLabel)}</div>
            <div class="font-semibold text-sm text-[var(--theme-secondary-ink)] text-left whitespace-nowrap">Vigência:</div>
            <div class="font-semibold text-sm text-[var(--theme-secondary-ink)] text-left whitespace-nowrap">Situação:</div>
            <div class="font-semibold text-sm text-[var(--theme-secondary-ink)] text-left whitespace-nowrap">Formato:</div>
          </div>
          ${renderPrintRows(documents, isExternalList)}
        </section>
      </main>
    </body>
  </html>
`;
